
import { useSurfaceSharePanelStore } from '@@/pinia/surface_share_panel'
import { isEmail } from 'bits/email'
import { isAppUsing } from 'bits/flip'
import { Component, Getter, namespace, State, Vue } from 'bits/vue'
import type { WallAccessRight } from 'enums'
import OzLoadingSpinner from 'library/v4/components/OzLoadingSpinner.vue'
import type { UserGroupWallCollaborator, UserWallCollaborator, WallAccessSettings } from 'types'
import AvatarImage from 'vuecomponents/avatar_image.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'
import ProgressIndeterminate from 'vuecomponents/progress_indeterminate.vue'
import SurfacePanelShareSubpanelMember from 'vuecomponents/surface_panel_share_subpanel_member.vue'
import { ShareSubpanel } from 'vuexstore/modules/share'

function key(collaborator: UserWallCollaborator): string {
  return collaborator.id ? collaborator.id.toString() : collaborator.email
}

const share = namespace('share')
const wallAccessSettings = namespace('wallAccessSettings')

@Component({
  components: {
    BaseIcon,
    AvatarImage,
    OzLoadingSpinner,
    ProgressIndeterminate,
    SurfacePanelShareSubpanelMember,
  },
})
class MembersPanel extends Vue {
  @State user

  @wallAccessSettings.State wallAccessSettings!: WallAccessSettings

  @Getter isOrgWall

  @wallAccessSettings.Getter isSearchingCollaborators!: boolean
  @wallAccessSettings.Getter userWallCollaboratorsSearchResult!: UserWallCollaborator[]
  @wallAccessSettings.Getter userGroupWallCollaboratorsSearchResult!: UserGroupWallCollaborator[]
  @wallAccessSettings.Getter collaboratorsSearchQuery!: string
  @wallAccessSettings.Getter areCollaboratorsDirty!: boolean
  @wallAccessSettings.Getter collaborators!: UserWallCollaborator[]
  @wallAccessSettings.Getter invitedCollaborators!: UserWallCollaborator[]
  @wallAccessSettings.Getter approvedCollaborators!: UserWallCollaborator[]
  @wallAccessSettings.Getter isSaving!: boolean
  @wallAccessSettings.Getter errorMessage!: string
  @wallAccessSettings.Getter isCollaboratorAlreadyAdded!: (collaborator: Partial<UserWallCollaborator>) => boolean
  @wallAccessSettings.Getter isCollaboratorAlreadyInvited!: (collaborator: Partial<UserWallCollaborator>) => boolean

  @share.Action confirmChangePanelDiscardChanges

  @wallAccessSettings.Action addCollaborator
  @wallAccessSettings.Action addCollaboratorByEmail
  @wallAccessSettings.Action debouncedSearchCollaborators
  @wallAccessSettings.Action removeCollaborator
  @wallAccessSettings.Action resendInvitation
  @wallAccessSettings.Action setCollaboratorRight
  @wallAccessSettings.Action updateWallAccessSettings

  key = key
  isAppUsing = isAppUsing

  get isSearching(): boolean {
    return this.collaboratorsSearchQuery.length > 0
  }

  get xMembersList(): boolean {
    return !this.isSearching && this.collaborators.length > 0
  }

  get xSearchResults(): boolean {
    if (isAppUsing('userGroups')) {
      return (
        this.isSearching &&
        (this.userWallCollaboratorsSearchResult.length > 0 || this.userGroupWallCollaboratorsSearchResult.length > 0)
      )
    } else {
      return this.isSearching && this.userWallCollaboratorsSearchResult.length > 0
    }
  }

  get xSearching(): boolean {
    if (isAppUsing('userGroups')) {
      return (
        this.isSearching &&
        this.userWallCollaboratorsSearchResult.length === 0 &&
        this.userGroupWallCollaboratorsSearchResult.length === 0 &&
        this.isSearchingCollaborators
      )
    } else {
      return this.isSearching && this.userWallCollaboratorsSearchResult.length < 1 && this.isSearchingCollaborators
    }
  }

  get xNobodyFound(): boolean {
    if (isAppUsing('userGroups')) {
      return (
        this.isSearching &&
        this.userWallCollaboratorsSearchResult.length === 0 &&
        this.userGroupWallCollaboratorsSearchResult.length === 0 &&
        !this.isSearchingCollaborators
      )
    } else {
      return this.isSearching && this.userWallCollaboratorsSearchResult.length < 1 && !this.isSearchingCollaborators
    }
  }

  get xCannotAddSelf(): boolean {
    return this.collaboratorsSearchQuery === this.user.email
  }

  get xAddByEmailButton(): boolean {
    return (
      // We don't allow adding new users by email address on org walls
      !this.isOrgWall &&
      !this.isSearchingCollaborators &&
      !!this.collaboratorsSearchQuery &&
      !this.xCannotAddSelf &&
      !this.isCollaboratorAlreadyAdded({ email: this.collaboratorsSearchQuery }) &&
      isEmail(this.collaboratorsSearchQuery)
    )
  }

  goBackToMainPanel(): void {
    this.confirmChangePanelDiscardChanges(ShareSubpanel.Main)
  }

  search(e): void {
    this.debouncedSearchCollaborators({ query: e.target.value })
  }

  setRight(right: WallAccessRight, collaborator: UserWallCollaborator): void {
    this.setCollaboratorRight({ collaborator, right })
  }

  updateMembers(): void {
    this.updateWallAccessSettings()
    if (isAppUsing('sharePanelV2')) {
      // To allow the old share panel's collaborator subpanel to update this new share panel's wall access settings
      // TODO: remove this once the old collaborator subpanel has been replaced by the new collaborator subpanel
      useSurfaceSharePanelStore().setWallAccessSettings(this.wallAccessSettings)
    }
  }

  mounted() {
    const searchInput = this.$refs.searchInput as HTMLInputElement
    // To prevent focus on objects that are out of the screen, which would result in an animation bug of the panel
    setTimeout(() => {
      searchInput?.focus()
    }, 250)
  }
}

export default MembersPanel
